import { observer } from 'mobx-react-lite'
import { SelectField } from 'shared/ui/Select/SelectField/SelectField'
import { TextareaWithLabel } from 'shared/ui/Textarea'
import { Box, Button, TextFieldWithLabel } from 'shared/ui'
import { useTollFreeStepsContext } from 'pages/settings/pages/compliance/pages/tollFree/context/tollFreeStepsContext'
import { PageDescription } from 'pages/settings/ui/PageDescription/PageDescription'
import { NextButton } from 'pages/settings/ui/NextButton/NextButton'
import { RegistrationErrorAlert } from 'pages/settings/pages/compliance/pages/tollFree/ui/RegistrationErrorAlert/RegistrationErrorAlert'
import {
  sampleMessageMaxTextLength,
  sampleMessageMinTextLength,
  useCaseDescMaxTextLength,
  useCaseDescMinTextLength,
} from 'pages/settings/pages/compliance/pages/tollFree/pages/UseCase/constants/constants.tsx'
import styles from './styles.module.scss'

export const UseCase = observer(() => {
  const {
    stepLoading,
    useCaseStore: {
      useCases,
      useCaseValue,
      useCaseDesc,
      isNextDisabled,
      setUseCase,
      setUseCaseDesc,
      openUseCaseExampleModal,
      nextStepClick,
      messageVolume,
      setMessageVolume,
      messageVolumeValue,
      sampleMessage,
      setSampleMessage,
      openSampleMessageModal,
      generateTollFreeAIDesc,
      generateTollFreeAIMessage,
      aiGenerationStore: { descLoading, messageLoading, istDescGenerated, isMessageGenerated },
      privacyPolicyUrl,
      setPrivacyPolicyUrl,
      errorPrivacyPolicyUrl,
      termsUrl,
      setTermsUrl,
      errorTermsUrl,
    },
  } = useTollFreeStepsContext()

  const descGenerationText = istDescGenerated ? 'Generate again' : 'Generate with Salesmsg AI'
  const messageGenerationText = isMessageGenerated ? 'Generate again' : 'Generate with Salesmsg AI'

  return (
    <div className={styles.wrap}>
      <PageDescription>
        Select the use case that best describes the type of messaging you plan to send.
      </PageDescription>

      <RegistrationErrorAlert />
      <Box marginTop={5}>
        <SelectField
          labelText='Estimated monthly messages'
          items={messageVolume}
          setValue={setMessageVolume}
          value={messageVolumeValue}
          btnProps={{
            size: 'extraLarge',
            className: styles.select,
          }}
          dropdownProps={{
            textFieldProps: {
              InputProps: {
                placeholder: 'Search',
              },
            },
          }}
        />
      </Box>
      <Box marginTop={3}>
        <SelectField
          labelText='Use case'
          items={useCases}
          setValue={setUseCase}
          value={useCaseValue}
          btnProps={{
            size: 'extraLarge',
            className: styles.select,
          }}
          dropdownProps={{
            textFieldProps: {
              InputProps: {
                placeholder: 'Search',
              },
            },
          }}
        />
      </Box>

      {useCaseValue && (
        <>
          <TextareaWithLabel
            className={styles.useCaseDesc}
            value={useCaseDesc}
            limit={useCaseDescMaxTextLength}
            minLimit={useCaseDescMinTextLength}
            errorType='bottomText'
            name='description'
            placeholder='Add a brief description of how the use case you selected applies for your business'
            onChange={setUseCaseDesc}
            labelProps={{
              marginBottom: 0,
              label: {
                text: 'Use case description',
                rightNode: (
                  <Button
                    onClick={openUseCaseExampleModal}
                    text={'See example'}
                    size='small'
                    typeBtn='informative'
                  />
                ),
              },
            }}
            bottomNode={{
              leftNode: (
                <Button
                  onClick={generateTollFreeAIDesc}
                  text={descLoading ? 'Generating...' : descGenerationText}
                  size='small'
                  typeBtn='informative'
                  icon='stars'
                  iconProps={{
                    color: 'var(--content-brand-primary)',
                  }}
                />
              ),
            }}
          />
          <TextareaWithLabel
            className={styles.sampleMessageDesc}
            value={sampleMessage}
            limit={sampleMessageMaxTextLength}
            minLimit={sampleMessageMinTextLength}
            errorType='bottomText'
            placeholder='Enter the most common message you send to your contacts'
            onChange={setSampleMessage}
            labelProps={{
              marginBottom: 0,
              label: {
                text: 'Sample message',
                rightText: 'See example',
                rightNode: (
                  <Button
                    onClick={openSampleMessageModal}
                    text={'See example'}
                    size='small'
                    typeBtn='informative'
                  />
                ),
              },
            }}
            bottomNode={{
              leftNode: (
                <Button
                  onClick={generateTollFreeAIMessage}
                  text={messageLoading ? 'Generating...' : messageGenerationText}
                  size='small'
                  typeBtn='informative'
                  icon='stars'
                  iconProps={{
                    color: 'var(--content-brand-primary)',
                  }}
                />
              ),
            }}
          />
          <TextFieldWithLabel
            marginTop={3}
            errorMessage={errorPrivacyPolicyUrl}
            labelProps={{
              marginBottom: 4,
              label: {
                text: 'Privacy Policy URL (optional)',
              },
            }}
            textFieldProps={{
              value: privacyPolicyUrl,
              onChange: setPrivacyPolicyUrl,
              InputProps: {
                placeholder: 'e.g. https://www.example.com/',
              },
              className: styles.textField,
            }}
          />
          <TextFieldWithLabel
            marginTop={3}
            marginBottom={0.5}
            errorMessage={errorTermsUrl}
            labelProps={{
              marginBottom: 4,
              label: {
                text: 'Terms and Conditions URL (optional)',
              },
            }}
            textFieldProps={{
              value: termsUrl,
              onChange: setTermsUrl,
              InputProps: {
                placeholder: 'e.g. https://www.example.com/',
              },
              className: styles.textField,
            }}
          />
        </>
      )}

      <NextButton
        loading={stepLoading}
        disabled={isNextDisabled || stepLoading}
        text='Next'
        onClick={nextStepClick}
        className={styles.nextButton}
      />
    </div>
  )
})
